import React, { useState, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, CheckCircle, ImagePlus, X } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import { productImages } from '../data/mockData';
export function UploadProduct() {
  const navigate = useNavigate();
  const [showSuccess, setShowSuccess] = useState(false);
  const [imagePreview, setImagePreview] = useState<string | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [formData, setFormData] = useState({
    type: 'COD CP',
    name: '',
    description: '',
    price: '',
    deliveryMethod: 'Manual'
  });
  const handleImageChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      const reader = new FileReader();
      reader.onloadend = () => {
        setImagePreview(reader.result as string);
      };
      reader.readAsDataURL(file);
    }
  };
  const removeImage = () => {
    setImagePreview(null);
    if (fileInputRef.current) fileInputRef.current.value = '';
  };
  const getPlaceholderImage = () => {
    return productImages[formData.type] || productImages['default'];
  };
  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    setShowSuccess(true);
    setTimeout(() => {
      navigate('/products', {
        state: {
          activeTab: 'pending'
        }
      });
    }, 2000);
  };
  return <div className="min-h-screen" data-id="element-437">
      <div className="max-w-md mx-auto" data-id="element-438">
        {/* Header */}
        <div className="flex items-center gap-4 p-5 pt-8" data-id="element-439">
          <button onClick={() => navigate('/products')} className="w-10 h-10 rounded-full glass-card flex items-center justify-center text-white hover:bg-white/[0.08] transition-colors" data-id="element-440">
            <ArrowLeft className="w-5 h-5" data-id="element-441" />
          </button>
          <h1 className="text-white text-2xl font-bold tracking-tight" data-id="element-442">
            Add Product
          </h1>
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit} className="px-5 space-y-5 pb-10" data-id="element-443">
          {/* Image Upload */}
          <div className="glass-card p-5" data-id="element-444">
            <label className="text-label mb-3 block" data-id="element-445">Product Image</label>
            <input ref={fileInputRef} type="file" accept="image/*" onChange={handleImageChange} className="hidden" id="product-image" data-id="element-446" />

            {imagePreview ? <div className="relative rounded-xl overflow-hidden" data-id="element-447">
                <img src={imagePreview} alt="Preview" className="w-full h-48 object-cover" data-id="element-448" />
                <button type="button" onClick={removeImage} className="absolute top-3 right-3 w-8 h-8 rounded-full bg-black/60 backdrop-blur-sm flex items-center justify-center text-white hover:bg-black/80 transition-colors" data-id="element-449">
                  <X className="w-4 h-4" data-id="element-450" />
                </button>
                <div className="absolute bottom-0 inset-x-0 bg-gradient-to-t from-black/60 to-transparent p-3" data-id="element-451">
                  <p className="text-white text-xs font-medium" data-id="element-452">
                    Image uploaded ✓
                  </p>
                </div>
              </div> : <label htmlFor="product-image" className="cursor-pointer block" data-id="element-453">
                <div className="relative rounded-xl overflow-hidden" data-id="element-454">
                  <img src={getPlaceholderImage()} alt="Placeholder" className="w-full h-48 object-cover opacity-40" data-id="element-455" />
                  <div className="absolute inset-0 flex flex-col items-center justify-center bg-black/30" data-id="element-456">
                    <div className="w-14 h-14 rounded-full bg-white/[0.1] backdrop-blur-sm flex items-center justify-center mb-3 border border-white/[0.15]" data-id="element-457">
                      <ImagePlus className="w-7 h-7 text-white" data-id="element-458" />
                    </div>
                    <p className="text-white font-bold tracking-tight" data-id="element-459">
                      Upload Product Image
                    </p>
                    <p className="text-white/60 text-xs mt-1" data-id="element-460">
                      Tap to browse files
                    </p>
                  </div>
                </div>
              </label>}
          </div>

          <div className="glass-card p-5 space-y-5" data-id="element-461">
            <div data-id="element-462">
              <label className="text-label mb-2 block" data-id="element-463">Product Type</label>
              <select value={formData.type} onChange={e => setFormData({
              ...formData,
              type: e.target.value
            })} className="w-full glass-input px-4 py-3.5 appearance-none" data-id="element-464">
                <option value="COD CP" className="bg-[#0A0E1A]" data-id="element-465">
                  COD CP
                </option>
                <option value="FIFA Coins" className="bg-[#0A0E1A]" data-id="element-466">
                  FIFA Coins
                </option>
                <option value="TikTok Account" className="bg-[#0A0E1A]" data-id="element-467">
                  TikTok Account
                </option>
                <option value="Game Account" className="bg-[#0A0E1A]" data-id="element-468">
                  Game Account
                </option>
              </select>
            </div>

            <div data-id="element-469">
              <label className="text-label mb-2 block" data-id="element-470">Product Name</label>
              <input type="text" value={formData.name} onChange={e => setFormData({
              ...formData,
              name: e.target.value
            })} placeholder="e.g., 10,000 COD CP Points" required className="w-full glass-input px-4 py-3.5 placeholder-[#475569]" data-id="element-471" />
            </div>

            <div data-id="element-472">
              <label className="text-label mb-2 block" data-id="element-473">Description</label>
              <textarea value={formData.description} onChange={e => setFormData({
              ...formData,
              description: e.target.value
            })} placeholder="Describe your product..." required rows={4} className="w-full glass-input px-4 py-3.5 placeholder-[#475569] resize-none" data-id="element-474" />
            </div>

            <div data-id="element-475">
              <label className="text-label mb-2 block" data-id="element-476">Price (₦)</label>
              <input type="number" value={formData.price} onChange={e => setFormData({
              ...formData,
              price: e.target.value
            })} placeholder="0" required min="0" className="w-full glass-input px-4 py-3.5 placeholder-[#475569] font-mono" data-id="element-477" />
            </div>

            <div data-id="element-478">
              <label className="text-label mb-2 block" data-id="element-479">Delivery Method</label>
              <select value={formData.deliveryMethod} onChange={e => setFormData({
              ...formData,
              deliveryMethod: e.target.value
            })} className="w-full glass-input px-4 py-3.5 appearance-none" data-id="element-480">
                <option value="Manual" className="bg-[#0A0E1A]" data-id="element-481">
                  Manual
                </option>
                <option value="Automatic" className="bg-[#0A0E1A]" data-id="element-482">
                  Automatic
                </option>
              </select>
            </div>
          </div>

          <motion.button whileTap={{
          scale: 0.97
        }} type="submit" className="w-full bg-[#00D68F] text-[#0A0E1A] rounded-2xl py-4 font-bold text-lg shadow-[0_0_20px_rgba(0,214,143,0.2)]" data-id="element-483">
            Submit Product
          </motion.button>
        </form>
      </div>

      {/* Success Modal */}
      <AnimatePresence data-id="element-484">
        {showSuccess && <motion.div initial={{
        opacity: 0
      }} animate={{
        opacity: 1
      }} exit={{
        opacity: 0
      }} className="fixed inset-0 bg-[#0A0E1A]/90 backdrop-blur-xl flex items-center justify-center z-50 px-5" data-id="element-485">
            <motion.div initial={{
          scale: 0.9,
          opacity: 0,
          y: 20
        }} animate={{
          scale: 1,
          opacity: 1,
          y: 0
        }} exit={{
          scale: 0.9,
          opacity: 0,
          y: 20
        }} className="glass-card border-white/[0.1] p-8 text-center max-w-sm w-full shadow-2xl" data-id="element-486">
              <motion.div initial={{
            scale: 0
          }} animate={{
            scale: 1
          }} transition={{
            delay: 0.2,
            type: 'spring',
            stiffness: 200
          }} className="w-20 h-20 rounded-full bg-[#00D68F]/10 flex items-center justify-center mx-auto mb-6" data-id="element-487">
                <CheckCircle className="w-10 h-10 text-[#00D68F]" style={{
              filter: 'drop-shadow(0 0 8px rgba(0,214,143,0.5))'
            }} data-id="element-488" />
              </motion.div>
              <h3 className="text-white text-2xl font-bold tracking-tight mb-2" data-id="element-489">
                Submitted!
              </h3>
              <p className="text-[#64748B]" data-id="element-490">Your product is under review</p>
            </motion.div>
          </motion.div>}
      </AnimatePresence>
    </div>;
}